import React, { Fragment } from 'react';
import { withStyles } from "tss-react/mui";
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import CardDisplay from '../CardDisplay';
import AlertPanel from 'components/AlertPanel';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import EventButton from 'components/EventButton';
import PropTypes from 'prop-types';
import DropDown from 'components/DropDown';
import FormElement from 'components/FormElement';
import { H2, Body1, Subtitle2, H4, Caption } from 'components/Typography';
import { toShortDateTimeString } from 'utilities/formatters/dateTimeFormatter';
import FatigueInformation from '../FatigueInformation';
import Restrictions from '../Restrictions';
import Breathalyser from '../Breathalyser';
import MissingCompetenciesView from '../MissingCompetenciesView';
import isNotNullOrEmpty from 'utilities/is/isNotNullOrEmpty';
import styles from './styles';
import m from 'messages';
import displayMode from 'areas/swipes/domain/displayMode';
import isStringSet from 'utilities/is/isStringSet';

class CardHolderDetails extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            selectedEmployerID: props.card.selectedEmployerID || '',
            selectedJobRoleID: props.card.selectedJobRoleID || ''
        };
    }

    employerChanged = (e) => {
        const employerId = e.target.value;
        this.props.card.selectedEmployerID = employerId;
        this.setState({ selectedEmployerID: employerId });

        if (this.props.hasOwnProperty('handleEmployerChanged')) {
            this.props.handleEmployerChanged(employerId);
        }
    };

    jobRoleChanged = (e) => {
        const jobRoleId = e.target.value;
        this.props.card.selectedJobRoleID = jobRoleId;
        this.setState({ selectedJobRoleID: jobRoleId });

        if (this.props.hasOwnProperty('handleJobRoleChanged')) {
            this.props.handleJobRoleChanged(jobRoleId);
        }
    };

    jobRolesToShow(jobRoles, currentDivisionId) {
        const filtered = (jobRoles || []).filter(j => !isStringSet(j.divisionID) || j.divisionID.toLowerCase() === currentDivisionId);        
        return filtered;
    }

    render() {
        const {
            card,
            classes,
            config,
            employerAndJobRoleEditable,
            showEmployerAndJobRole,
            fatigueDataEditable,
            location,
            viewcontext,
            denyReason
        } = this.props;

        const isRejected = Boolean(denyReason) && Boolean(denyReason.reasonDescription);
        const isSwipeOut = viewcontext === displayMode.swipeOut;
        const isBlocked = isNotNullOrEmpty(card.suspensions)
            && !(card.isSubscriptionExpired || card.isSubscriptionPending)
            && !isRejected
            && !isSwipeOut;

        const isCompetenceView =
            viewcontext === displayMode.awardCompetence
            || viewcontext === displayMode.awardPendingCompetence
            || viewcontext === displayMode.showCardAwardCompetence;

        const showMissingCompetencies = card.missingCompetences
            && viewcontext !== displayMode.awardCompetence
            && viewcontext !== displayMode.awardPendingCompetence
            && viewcontext !== displayMode.showCardAwardCompetence
            && (isRejected && card.missingCompetences);

        const showFatigueData =
            !card.isMinimalCardDisplay
            && !isCompetenceView
            && card.fatigueInformation
            && !isRejected;

        const jobRolesToShow = this.jobRolesToShow(card.jobRolesArray, location.division.divisionID.toLowerCase());

        return (
            <Grid container>
                {showMissingCompetencies &&
                    <Grid item xs={12}>
                        <MissingCompetenciesView card={card} config={config} location={location} />
                    </Grid>
                }
                <Grid item xs={12}>
                    {isBlocked &&
                        <AlertPanel
                            message={m.cards.blocksPresentAlert}
                            variant="warning"
                            className={classes.alertPanel}
                            action={this.props.showBlockLink && [
                                <EventButton
                                    key="close"
                                    aria-label="Close"
                                    color="inherit"
                                    className={classes.close}
                                    onClick={this.props.onShowBlocks}>
                                    {m.general.show}
                                    <ArrowRightIcon className={classes.icon} />
                                </EventButton>
                            ]}
                        />
                    }
                </Grid>
                <Grid item xs={12}>
                    <H2 paragraph>
                        {m.cards.cardDetails}
                    </H2>
                </Grid>

                <Grid container spacing={2} className={classes.margin}>
                    <Grid item xs={12}>
                        <H4 paragraph>{m.cards.cardholderOverviewHeader}</H4>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={4}>
                        <CardDisplay card={card} />
                        {card.newToIndustry &&
                            <Caption paragraph className={classes.newToIndustry}>
                                {m.cards.newToIndustry}
                            </Caption>
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={8} lg={8}>
                        <Grid container spacing={0}>
                            <Grid item sm={12} md={12} lg={4} className={classes.dropdown}>
                                <Subtitle2>
                                    {m.cards.checkedWithDatabase}
                                </Subtitle2>
                                <Body1 paragraph>
                                    {toShortDateTimeString(card.dateRetrieved)}
                                </Body1>
                            </Grid>
                            {showEmployerAndJobRole && !isRejected &&
                                <Fragment>
                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <FormElement className={classes.dropdown} fullWidth>
                                            <Subtitle2>
                                                {m.cards.employer}
                                            </Subtitle2>
                                            {employerAndJobRoleEditable &&
                                                <DropDown
                                                    id="employer"
                                                    value={card.selectedEmployerID || ''}
                                                    onChange={this.employerChanged}>
                                                    {card.employersArray.map((item, index) =>
                                                        <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                                    )}
                                                </DropDown>
                                            }
                                            {!employerAndJobRoleEditable && card.employer &&
                                                <Body1 paragraph>{card.employer.name}</Body1>
                                            }
                                        </FormElement>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} lg={4}>
                                        <FormElement className={classes.dropdown} fullWidth>
                                            <Subtitle2>
                                                {m.cards.jobRole}
                                            </Subtitle2>
                                            {employerAndJobRoleEditable &&
                                                <DropDown
                                                    value={card.selectedJobRoleID || ''}
                                                    onChange={this.jobRoleChanged}>

                                                    {jobRolesToShow.map((item, index) =>
                                                        <MenuItem key={index} value={item.id}>{item.displayName}</MenuItem>
                                                    )}
                                                </DropDown>
                                            }
                                            {!employerAndJobRoleEditable && card.jobRole &&
                                                <Body1 paragraph>{card.jobRole.displayName}</Body1>
                                            }
                                        </FormElement>
                                    </Grid>
                                </Fragment>
                            }
                        </Grid>
                    </Grid>
                </Grid>

                {!card.isMinimalCardDisplay && !isSwipeOut && !card.isSuspendedBySite &&
                    <Breathalyser card={card} />
                }

                {!card.isMinimalCardDisplay && !isSwipeOut &&
                    <Restrictions card={card} />
                }

                {showFatigueData &&
                    <div className={classes.fatiguePanel}>
                        <FatigueInformation
                            editable={fatigueDataEditable}
                            fatigueInformation={card.fatigueInformation}
                            timeToWorkChanged={this.props.timeToWorkChanged}
                            timeFromWorkChanged={this.props.timeFromWorkChanged}
                            fuelTypeFromWorkChanged={this.props.fuelTypeFromWorkChanged}
                            fuelTypeToWorkChanged={this.props.fuelTypeToWorkChanged}
                            travelMethodFromWorkChanged={this.props.travelMethodFromWorkChanged}
                            travelMethodToWorkChanged={this.props.travelMethodToWorkChanged}
                            travelDistanceFromWorkChanged={this.props.travelDistanceFromWorkChanged}
                            travelDistanceToWorkChanged={this.props.travelDistanceToWorkChanged}
                            config={config}
                        />

                    </div>
                }

            </Grid>);
    }
}

CardHolderDetails.propTypes = {
    card: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    denyReason: PropTypes.object,
    employerAndJobRoleEditable: PropTypes.bool,
    showEmployerAndJobRole: PropTypes.bool,
    fatigueDataEditable: PropTypes.bool,
    handleEmployerChanged: PropTypes.func,
    handleJobRoleChanged: PropTypes.func
};

export default withStyles(CardHolderDetails, styles);